import { useEffect, useState, useMemo } from "react";
import { Container } from "@/components/ui/container";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { GuideCard } from "@/components/GuideCard";
import { TableOfContents } from "@/components/TableOfContents";
import { useSeo, generateBreadcrumbSchema, generateArticleSchema, generateHowToSchema } from "@/hooks/useSeo";
import type { Guide } from "@/data/guides";
import { formatDate, guideKategorien } from "@/data/guides";
import { Calendar, Clock, User, ArrowLeft, Lightbulb } from "lucide-react";

interface GuideSeiteProps {
  slug: string;
  onNavigate: (path: string) => void;
}

export function GuideSeite({ slug, onNavigate }: GuideSeiteProps) {
  const [guide, setGuide] = useState<Guide | null>(null);
  const [related, setRelated] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(true);

  // Generate TOC items from guide sections
  const tocItems = useMemo(() => {
    if (!guide) return [];
    return guide.inhalt.abschnitte.map((abschnitt, index) => ({
      id: `section-${index}`,
      text: abschnitt.ueberschrift,
      level: 2,
    }));
  }, [guide]);

  useEffect(() => {
    // Scroll to top when navigating to a new guide
    window.scrollTo(0, 0);

    setLoading(true);

    // Fetch guide
    fetch(`/api/guides/${slug}`)
      .then(res => res.json())
      .then(data => {
        setGuide(data);
        setLoading(false);

        // Fetch related guides
        return fetch(`/api/guides/${slug}/related`);
      })
      .then(res => res?.json())
      .then(data => {
        if (data) setRelated(data);
      })
      .catch(() => setLoading(false));
  }, [slug]);

  // Calculate word count from guide content
  const wordCount = useMemo(() => {
    if (!guide) return 0;
    const allText = [
      guide.inhalt.einleitung,
      ...guide.inhalt.abschnitte.map(a => a.inhalt + (a.liste?.join(" ") || "") + (a.tipp || "")),
      guide.inhalt.fazit
    ].join(" ");
    return allText.split(/\s+/).length;
  }, [guide]);

  // SEO
  const kategorieName = guide ? guideKategorien.find(k => k.id === guide.kategorie)?.name : "";

  // Check if guide has numbered steps (how-to pattern)
  const isHowTo = guide?.inhalt.abschnitte.some(a => /^\d+\./.test(a.ueberschrift)) ?? false;

  useSeo(
    guide
      ? {
          title: `${guide.titel} | Ratgeber | zelt.kaufen`,
          description: guide.beschreibung,
          keywords: `${guide.titel}, ${kategorieName}, ${guide.tags.join(", ")}, Camping Ratgeber, Zelt Guide`,
          image: guide.bild,
          type: "article",
          schema: {
            "@context": "https://schema.org",
            "@graph": [
              generateArticleSchema({
                headline: guide.titel,
                description: guide.beschreibung,
                image: guide.bild,
                datePublished: guide.datum,
                dateModified: guide.datum,
                author: {
                  name: guide.autor.name,
                  role: guide.autor.rolle,
                },
                category: kategorieName,
                keywords: guide.tags,
                wordCount,
              }),
              generateBreadcrumbSchema([
                { name: "Startseite", url: window.location.origin },
                { name: "Ratgeber", url: `${window.location.origin}/ratgeber` },
                { name: guide.titel, url: window.location.href },
              ]),
              ...(isHowTo
                ? [
                    generateHowToSchema({
                      name: guide.titel,
                      description: guide.beschreibung,
                      image: guide.bild,
                      steps: guide.inhalt.abschnitte.map(a => ({
                        name: a.ueberschrift.replace(/^\d+\.\s*/, ""),
                        text: a.inhalt + (a.liste ? " " + a.liste.join(". ") : ""),
                      })),
                    }),
                  ]
                : []),
            ],
          },
        }
      : {
          title: "Laden... | Ratgeber | zelt.kaufen",
          description: "Ratgeber wird geladen",
        }
  );

  if (loading) {
    return (
      <Container className="py-8">
        <div className="animate-pulse max-w-4xl mx-auto">
          <div className="h-4 bg-muted rounded w-32 mb-4" />
          <div className="h-10 bg-muted rounded w-3/4 mb-8" />
          <div className="aspect-[21/9] bg-muted rounded-lg mb-8" />
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-4 bg-muted rounded" />
            ))}
          </div>
        </div>
      </Container>
    );
  }

  if (!guide) {
    return (
      <Container className="py-16 text-center">
        <h1 className="text-2xl font-bold mb-4">Ratgeber nicht gefunden</h1>
        <Button onClick={() => onNavigate("/ratgeber")}>Zu allen Ratgebern</Button>
      </Container>
    );
  }

  const kategorie = guideKategorien.find(k => k.id === guide.kategorie);

  return (
    <>
      {/* Hero */}
      <div className="bg-muted/30 border-b">
        <Container className="py-8">
          <Breadcrumb
            items={[
              { label: "Ratgeber", href: "/ratgeber" },
              { label: kategorie?.name || "", href: `/ratgeber?kategorie=${guide.kategorie}` },
              { label: guide.titel },
            ]}
            className="mb-6"
          />

          <div className="max-w-4xl">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onNavigate("/ratgeber")}
              className="mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zurück zur Übersicht
            </Button>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-4">
              {kategorie && <Badge variant="secondary">{kategorie.name}</Badge>}
              {guide.tags.map(tag => (
                <Badge key={tag} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{guide.titel}</h1>
            <p className="text-lg text-muted-foreground mb-6">{guide.beschreibung}</p>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <div>
                  <span className="font-medium text-foreground">{guide.autor.name}</span>
                  <span className="text-xs ml-1">· {guide.autor.rolle}</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(guide.datum)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{guide.lesezeit} Min. Lesezeit</span>
              </div>
            </div>
          </div>
        </Container>
      </div>

      {/* Content */}
      <Container className="py-12">
        <div className="max-w-7xl mx-auto flex gap-12">
          <article className="flex-1 min-w-0">
          {/* Hero Image */}
          <div className="aspect-[21/9] rounded-lg overflow-hidden mb-12">
            <img
              src={guide.bild}
              alt={guide.titel}
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>

          {/* Einleitung */}
          <div className="prose prose-lg dark:prose-invert max-w-none mb-12">
            <p className="lead text-xl text-muted-foreground">{guide.inhalt.einleitung}</p>
          </div>

          {/* Abschnitte */}
          <div className="space-y-10">
            {guide.inhalt.abschnitte.map((abschnitt, index) => (
              <section key={index} id={`section-${index}`} className="scroll-mt-24">
                <h2 className="text-2xl font-bold mb-4">{abschnitt.ueberschrift}</h2>
                <p className="text-muted-foreground mb-4 leading-relaxed whitespace-pre-line">
                  {abschnitt.inhalt}
                </p>

                {/* Liste */}
                {abschnitt.liste && abschnitt.liste.length > 0 && (
                  <ul className="space-y-2 mb-4">
                    {abschnitt.liste.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Tipp */}
                {abschnitt.tipp && (
                  <div className="bg-primary/5 border-l-4 border-primary rounded-r-lg p-4 my-4">
                    <div className="flex gap-3">
                      <Lightbulb className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-sm mb-1">Profi-Tipp</p>
                        <p className="text-sm text-muted-foreground">{abschnitt.tipp}</p>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            ))}
          </div>

          {/* Fazit */}
          <div className="mt-12 p-6 bg-muted/50 rounded-lg border">
            <h2 className="text-xl font-bold mb-3">Fazit</h2>
            <p className="text-muted-foreground leading-relaxed">{guide.inhalt.fazit}</p>
          </div>
        </article>

        {/* Table of Contents */}
        <aside className="w-64 flex-shrink-0">
          <TableOfContents items={tocItems} />
        </aside>
      </div>
      </Container>

      {/* Related Guides */}
      {related.length > 0 && (
        <div className="bg-muted/30 border-t py-16">
          <Container>
            <h2 className="text-2xl font-bold mb-8">Weitere interessante Ratgeber</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map(relGuide => (
                <GuideCard key={relGuide.id} guide={relGuide} onNavigate={onNavigate} />
              ))}
            </div>
          </Container>
        </div>
      )}
    </>
  );
}
